import CONSTANTS from '../../../shared/constants';
import {
  findOptimalMidrollPlacement,
  ONLY_MIDROLL_TARGET_PERCENTAGE,
  SECOND_MIDROLL_TARGET_PERCENTAGE,
  sortMarkers,
} from '../../../shared/utils/adMarkerPlacementUtils';

export const MARKER_POSITIONS = {
  preroll: [0, 0.25],
  midroll: [0.45, 0.55],
  midroll2: [0.65, 0.9],
  postroll: [0.5, 1],
};

export const AD_MARKER_MARGIN = 0.001;

export const getDefaultStarts = ({ episodeDuration, placement, silences, markers }) => {
  if (typeof episodeDuration !== 'number' || episodeDuration <= 0 || !placement || !markers) {
    return null;
  }

  if (placement === 'preroll') {
    return MARKER_POSITIONS.preroll[0] * episodeDuration;
  }
  if (placement === 'postroll') {
    return MARKER_POSITIONS.postroll[1] * episodeDuration;
  }

  if (placement.includes('midroll')) {
    const existingMidrolls = markers.filter(({ placement }) => placement.includes('midroll'));
    const isPlacingFirstMidroll = !existingMidrolls.length;

    const prerollMarker = markers.find((m) => m.placement === 'preroll');
    const previousMarkerPos = isPlacingFirstMidroll
      ? (prerollMarker?.start ?? 0)
      : existingMidrolls[0].start;

    let targetPercentage;
    if (isPlacingFirstMidroll) {
      targetPercentage = ONLY_MIDROLL_TARGET_PERCENTAGE;
    } else {
      targetPercentage = SECOND_MIDROLL_TARGET_PERCENTAGE;
    }

    return findOptimalMidrollPlacement(
      silences || [],
      targetPercentage,
      episodeDuration,
      previousMarkerPos
    );
  }
};

export const generateDefaultMarkers = ({ markers = [], duration }) => {
  const midRolls = markers.filter(({ placement }) => placement === 'midroll');

  // If there are two midroll markers, mark the last one as 'midroll2'
  if (midRolls.length === 2) {
    const lastMidRollIndex = markers.findIndex((marker) => marker._id === midRolls[1]._id);
    if (lastMidRollIndex !== -1) {
      markers[lastMidRollIndex].placement = 'midroll2';
    }
  }

  if (!markers.some(({ placement }) => placement === 'preroll')) {
    markers.push({ placement: 'preroll', start: 0 });
  }
  if (!markers.some(({ placement }) => placement === 'postroll')) {
    markers.push({ placement: 'postroll', start: duration });
  }
  return markers;
};

export const getMarkers = (markers, episodeDuration, show) => {
  const firstMidrollDurationThreshold =
    show?.midrollDurationThreshold || CONSTANTS.FIRST_MIDROLL_DURATION_THRESHOLD;
  const secondMidrollDurationThreshold =
    show?.secondMidrollThreshold || CONSTANTS.SECOND_MIDROLL_DURATION_THRESHOLD;

  const currentEpisodeMarkers = markers.map(({ placement }) => placement);
  const allMarkersName = Object.keys(MARKER_POSITIONS);

  const suggestedMarkers = currentEpisodeMarkers
    .filter((val) => !allMarkersName.includes(val))
    .concat(
      allMarkersName.filter((val) => {
        if (val === 'midroll' && episodeDuration < firstMidrollDurationThreshold) {
          return false;
        }
        if (val === 'midroll2' && episodeDuration < secondMidrollDurationThreshold) {
          return false;
        }
        return !currentEpisodeMarkers.includes(val);
      })
    )
    .map((suggestedMarkers) => ({
      placement: suggestedMarkers,
      start: getDefaultStarts({ episodeDuration, placement: suggestedMarkers, markers }),
    }));

  const sortedMarkers = sortMarkers(markers.concat(suggestedMarkers));

  return sortedMarkers;
};

export const isMissingMidRoll = (
  markers,
  episodeDuration,
  midrollDurationThreshold = CONSTANTS.FIRST_MIDROLL_DURATION_THRESHOLD
) => {
  return (
    episodeDuration > midrollDurationThreshold &&
    markers.filter((m) => m.placement === 'midroll').length === 0
  );
};

export const getMarkerEndpoints = (markers, markerPlacement, episodeDuration) => {
  const marker = markers.find((m) => m.placement === markerPlacement);

  if (marker.placement === 'preroll') {
    // preroll should be always before the quarter of the episode, and before any midroll
    const sortedMidrolls = markers
      .filter((m) => m.placement.includes('midroll'))
      .sort((a, b) => a.start - b.start);
    const firstMidroll = sortedMidrolls.length ? sortedMidrolls[0] : null;
    const defaultLimit = episodeDuration * 0.25;
    const midrollLimit = firstMidroll && firstMidroll.start - AD_MARKER_MARGIN;
    const max = midrollLimit ? Math.min(midrollLimit, defaultLimit) : defaultLimit;
    return [0, max];
  }

  if (marker.placement === 'midroll' || marker.placement === 'midroll2') {
    // midroll are alway between the preroll and postroll
    const preroll = markers.find((m) => m.placement === 'preroll');
    const postroll = markers.find((m) => m.placement === 'postroll');

    const min = (preroll && preroll.start + AD_MARKER_MARGIN) || 0;
    const max = (postroll && postroll.start - AD_MARKER_MARGIN) || episodeDuration;
    return [min, max];
  }

  if (marker.placement === 'postroll') {
    // postroll should be always after the half of the episode, and after any midroll
    const sortedMidrolls = markers
      .filter((m) => m.placement.includes('midroll'))
      .sort((a, b) => a.start - b.start);
    const lastMidroll = sortedMidrolls.length ? sortedMidrolls[sortedMidrolls.length - 1] : null;
    const defaultLimit = episodeDuration * 0.5;
    const midrollLimit = lastMidroll && lastMidroll.start + AD_MARKER_MARGIN;
    const min = midrollLimit ? Math.max(midrollLimit, defaultLimit) : defaultLimit;
    return [min, episodeDuration];
  }

  return [0, episodeDuration];
};
